/* Closeout uploads: straight from the browser to Facility Navigator's file storage, the way FN's own
   web page does it (app-rebuild apps/web lib/closeoutApi.ts + state/uploads.tsx — ported, not
   reinvented). Summit's server only asks FN for a short-lived upload link, then records the document.

   🔴 THE BROWSER'S PUTS MAY SEND ONLY x-ms-blob-type, x-ms-blob-content-type AND content-type. The
   storage account's CORS rule allows exactly those for Summit's address (FN chat, 2026-10-10); any
   other header fails EVERY upload, not just the big ones. Preflight a new header against the account
   before adding it.

   🔴 CHUNKED, AND IT MUST STAY CHUNKED. FN learned it the hard way (2026-09-14): a district network's
   proxy cut a 54 MB single PUT off near the end, four times. Files over 4 MiB go up as 4 MiB blocks,
   each retried on its own, then one commit; nothing is readable at the key until that commit.

   The queue lives here, outside the tab, so leaving the tab doesn't throw away what is uploading:
   transfers carry on, and the tab shows them again when it comes back. */
import { getCloseoutUploadUrl, commitCloseoutDoc } from '../../api';

const BLOCK_BYTES = 4 * 1024 * 1024;
const SINGLE_SHOT_MAX = BLOCK_BYTES;
const BLOCK_ATTEMPTS = 4;
// No upload progress for this long ⇒ the connection is dead; retry the block. Once the body is sent
// no progress events arrive, so the wait for Azure's answer gets the longer allowance.
const STALL_MS = 60000;
const RESPONSE_MS = 120000;
export const MAX_BATCH = 300;

class TransferError extends Error {
  constructor(message, status, retryable) {
    super(message);
    this.status = status;
    this.retryable = retryable;
  }
}

/** Our params go BEFORE the SAS; the signature covers the path, not the query (as the Azure SDK does). */
function withQuery(uploadUrl, extra) {
  const params = Object.entries(extra).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  const q = uploadUrl.indexOf('?');
  return q < 0 ? `${uploadUrl}?${params}` : `${uploadUrl.slice(0, q)}?${params}&${uploadUrl.slice(q + 1)}`;
}

/** Block ids must be base64 and the same length for every block of one blob. */
const blockId = (n) => btoa(`fn-block-${String(n).padStart(6, '0')}`);

/** One PUT with a stall watchdog. Rejects with a TransferError that knows whether a retry can help. */
function putOnce(url, body, headers, onBytes) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let ticked = Date.now();
    let sending = true;
    let stalled = false;
    const watchdog = window.setInterval(() => {
      if (Date.now() - ticked < (sending ? STALL_MS : RESPONSE_MS)) return;
      stalled = true;
      xhr.abort();
    }, 5000);
    const stop = () => window.clearInterval(watchdog);

    xhr.open('PUT', url, true);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      ticked = Date.now();
      if (e.lengthComputable && onBytes) onBytes(e.loaded);
    };
    xhr.upload.onload = () => { sending = false; ticked = Date.now(); };
    xhr.onload = () => {
      stop();
      if (xhr.status >= 200 && xhr.status < 300) { resolve(); return; }
      // 403 = an expired or malformed SAS: the same link fails the same way, so don't retry it.
      const retryable = xhr.status === 408 || xhr.status === 429 || xhr.status >= 500;
      reject(new TransferError(`storage returned ${xhr.status}`, xhr.status, retryable));
    };
    xhr.onerror = () => { stop(); reject(new TransferError('the connection was cut off', 0, true)); };
    xhr.onabort = () => {
      stop();
      reject(stalled ? new TransferError('the connection stopped responding', 0, true) : new TransferError('cancelled', 0, false));
    };
    try {
      xhr.send(body);
    } catch (e) {
      stop();
      reject(new TransferError((e && e.message) || 'the upload could not be started', 0, false));
    }
  });
}

async function withRetry(run) {
  let last;
  for (let attempt = 1; attempt <= BLOCK_ATTEMPTS; attempt++) {
    try {
      return await run();
    } catch (e) {
      last = e;
      if (!(e instanceof TransferError) || !e.retryable || attempt === BLOCK_ATTEMPTS) throw e;
      // Jittered backoff, so a batch doesn't retry in lockstep and recreate the burst.
      const wait = Math.min(8000, 500 * 2 ** (attempt - 1)) + Math.floor(Math.random() * 400);
      await new Promise((r) => setTimeout(r, wait));
    }
  }
  throw last;
}

/** Stage the file as blocks, then commit. Held below 100% until the commit lands. */
async function transferToBlob(uploadUrl, file, contentType, onProgress) {
  const total = file.size;
  const pctOf = (bytes) => Math.max(0, Math.min(99, Math.round((bytes / Math.max(total, 1)) * 100)));

  if (total <= SINGLE_SHOT_MAX) {
    await withRetry(() => putOnce(uploadUrl, file, {
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-blob-content-type': contentType,
    }, (loaded) => onProgress(pctOf(loaded))));
    onProgress(100);
    return;
  }

  const ids = [];
  let sent = 0;
  for (let i = 0; sent < total; i++) {
    const from = sent;
    const to = Math.min(from + BLOCK_BYTES, total);
    const id = blockId(i);
    ids.push(id);
    // Re-sliced from the File on every attempt, so a retry resends exactly these bytes.
    await withRetry(() => putOnce(withQuery(uploadUrl, { comp: 'block', blockid: id }), file.slice(from, to), {},
      (loaded) => onProgress(pctOf(from + loaded))));
    sent = to;
    onProgress(pctOf(sent));
  }
  const xml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids.map((id) => `<Latest>${id}</Latest>`).join('')}</BlockList>`;
  await withRetry(() => putOnce(withQuery(uploadUrl, { comp: 'blocklist' }), xml, {
    'content-type': 'application/xml',
    'x-ms-blob-content-type': contentType,
  }));
  onProgress(100);
}

/** Say what actually happened — not a guess at the cause. */
function transferMessage(e, reachedPct) {
  if (!(e instanceof TransferError)) return (e && e.message) || 'Upload failed.';
  if (e.message === 'cancelled') return 'Upload cancelled.';
  if (e.status === 403) return 'File storage rejected the upload (403): the upload link expired before the file finished. Try it again.';
  if (e.status > 0) return `File storage refused the upload (${e.status}).`;
  return `Upload stopped at ${reachedPct}% — ${e.message}, and ${BLOCK_ATTEMPTS} attempts couldn't get past it. `
    + 'That is usually a proxy, content filter or VPN on this network blocking large uploads rather than anything wrong with the file; '
    + 'the same file over a different connection (a phone hotspot, say) normally goes straight up.';
}

// Some browsers hand over a .mov with no type; name videos from the extension so previews play.
const VIDEO_BY_EXT = { mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm' };
export const videoTypeOfName = (fileName) => {
  const m = /\.([a-z0-9]+)$/.exec(String(fileName || '').toLowerCase());
  return (m && VIDEO_BY_EXT[m[1]]) || null;
};

/** One file, start to finish: an upload link from FN (via Summit), the transfer, then the record. */
async function uploadOne(projectId, file, folderId, onProgress) {
  const contentType = file.type || videoTypeOfName(file.name) || 'application/octet-stream';
  // `category` is FN's legacy column (folders replaced it); FN's own uploads write "other".
  const { uploadUrl, blobKey } = await getCloseoutUploadUrl(projectId, { fileName: file.name, contentType, category: 'other', sizeBytes: file.size });
  let reached = 0;
  try {
    await transferToBlob(uploadUrl, file, contentType, (pct) => { reached = pct; onProgress(pct); });
  } catch (e) {
    throw new Error(transferMessage(e, reached));
  }
  return commitCloseoutDoc(projectId, { blobKey, fileName: file.name, contentType, sizeBytes: file.size, category: 'other', folderId: folderId == null ? null : folderId });
}

// ── the queue ──────────────────────────────────────────────────────────────
let batches = []; // { id, projectId, label, rows: [{ id, name, pct, status: waiting|uploading|done|error, error }] }
const listeners = new Set();
const retries = new Map(); // rowId → () => Promise (same File, same destination)
const emit = () => { for (const fn of listeners) fn(batches); };

export function subscribeUploads(fn) {
  listeners.add(fn);
  fn(batches);
  return () => listeners.delete(fn);
}

function patchRow(rowId, patch) {
  batches = batches.map((b) => (b.rows.some((r) => r.id === rowId)
    ? { ...b, rows: b.rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r)) }
    : b));
  emit();
}

/**
 * Upload a picked set into one Summit job's library. `folderFor(dir)` returns the folder id for a
 * file's sub-path (creating folders for a dropped directory tree). Files go one at a time; a failure
 * stays on its row with Retry and never stops the rest.
 */
export function startUploads({ projectId, label, picked, folderFor, onLanded }) {
  const batchId = `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  const jobs = picked.map(({ file, dir }) => ({ file, dir, rowId: `${batchId}/${dir}/${file.name}-${file.size}-${Math.random().toString(36).slice(2)}` }));
  batches = [...batches, {
    id: batchId, projectId, label,
    rows: jobs.map((j) => ({ id: j.rowId, name: j.dir ? `${j.dir}/${j.file.name}` : j.file.name, pct: 0, status: 'waiting' })),
  }];
  emit();
  const runOne = async (j) => {
    patchRow(j.rowId, { status: 'uploading', pct: 0, error: undefined });
    try {
      const folderId = await folderFor(j.dir);
      await uploadOne(projectId, j.file, folderId, (pct) => patchRow(j.rowId, { pct }));
      patchRow(j.rowId, { status: 'done', pct: 100 });
      retries.delete(j.rowId);
      if (onLanded) onLanded();
    } catch (e) {
      patchRow(j.rowId, { status: 'error', error: (e && e.message) || 'Upload failed.' });
      retries.set(j.rowId, () => runOne(j));
    }
  };
  (async () => { for (const j of jobs) await runOne(j); })();
}

export function retryUpload(rowId) {
  const run = retries.get(rowId);
  if (run) run();
}

export function dismissBatch(batchId) {
  const b = batches.find((x) => x.id === batchId);
  if (b) b.rows.forEach((r) => retries.delete(r.id));
  batches = batches.filter((x) => x.id !== batchId);
  emit();
}

const uploadsActive = () => batches.some((b) => b.rows.some((r) => r.status === 'waiting' || r.status === 'uploading'));
// Closing or reloading the tab stops a transfer mid-file, so the browser asks first.
window.addEventListener('beforeunload', (e) => {
  if (!uploadsActive()) return;
  e.preventDefault();
  e.returnValue = '';
});

// ── picking files: an input, or a drop (which may hold whole folders) ─────────
const isJunkFile = (n) => n.startsWith('.') || n === 'Thumbs.db';

// readEntries returns AT MOST 100 per call and ends with an empty batch — one call truncates big folders.
function readAllEntries(reader) {
  return new Promise((resolve, reject) => {
    const all = [];
    const pump = () => reader.readEntries((batch) => {
      if (!batch.length) { resolve(all); return; }
      all.push(...batch);
      pump();
    }, reject);
    pump();
  });
}

async function walkEntry(entry, prefix, out) {
  if (entry.isFile && entry.file) {
    if (isJunkFile(entry.name)) return;
    const file = await new Promise((ok, err) => entry.file(ok, err));
    out.push({ file, dir: prefix });
    return;
  }
  if (entry.isDirectory && entry.createReader) {
    const dir = prefix ? `${prefix}/${entry.name}` : entry.name;
    for (const child of await readAllEntries(entry.createReader())) await walkEntry(child, dir, out);
  }
}

/** Files from a drop. Directory entries must be captured synchronously in the drop handler. */
export async function pickedFromEntries(entries, fallback) {
  if (!entries.length) return Array.from(fallback || []).filter((f) => !isJunkFile(f.name)).map((file) => ({ file, dir: '' }));
  const out = [];
  for (const e of entries) await walkEntry(e, '', out);
  return out;
}

/** Files from an <input type=file> (with webkitdirectory, each carries its relative path). */
export function pickedFromInput(list) {
  return Array.from(list || []).filter((f) => !isJunkFile(f.name)).map((file) => {
    const rel = file.webkitRelativePath || '';
    return { file, dir: rel.includes('/') ? rel.slice(0, rel.lastIndexOf('/')) : '' };
  });
}
